import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  Box,
  Tooltip,
  LinearProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  LuFileText,
  LuZap,
  LuFlame,
  LuGauge,
  LuInfo,
  LuTruck,
  LuRepeat,
  LuSnowflake,
  LuCalendar,
  LuSplit,
  LuCheck,
  LuWaves,
  LuTrash2,
  LuPlane,
  LuPackage,
} from "react-icons/lu";
import { useTranslation } from "react-i18next";
import {
  M3Button,
  M3IconButton,
  Chip,
  M3Badge,
  M3Field,
  SegmentedButton,
  ModeSelector,
  KpiGrid,
  KpiTile,
  Banner,
} from "../m3";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { EvidenceAnalysis } from "../../db_access/evidence_analysis";
import { useContextProvider } from "../page-context/ContextProvider";
import AgGrid from "../grid/AgGrid";
import {
  leftAlignColDef,
  rightAlignColDef,
  centerAlignColDef,
} from "../grid/columnDefinitions/columnDefinitions";

const ACCEPTED_FILE_TYPES = ".pdf";

export const SCOPE_DEFINITIONS = {
  "Scope 1": {
    label: "Scope 1 Direct Fuels & Combustion",
    documentNoun: "Scope 1 Bills & Invoices",
    categories: [
      {
        id: "stationary_combustion",
        name: "Stationary combustion",
        label: "Stationary Combustion",
        desc: "Boilers, furnaces, heating & backup generators",
        icon: <LuFlame size={20} color="#ef4444" />,
        accent: "#ef4444",
        categoryType: "utility",
        subtypes: [
          { name: "Natural Gas (m³)", unit: "m³", factor: 0.00193, factorUnit: "tCO₂e/m³", desc: "Pipeline gas per m³ (Canada NIR 1.93 kg/m³)", fuelType: "natural_gas" },
          { name: "Natural Gas (kWh)", unit: "kWh", factor: 0.000183, factorUnit: "tCO₂e/kWh", desc: "Natural gas energy equivalent (1 m³ = 10.55 kWh)", fuelType: "natural_gas" },
          { name: "Diesel #2", unit: "L", factor: 0.00268, factorUnit: "tCO₂e/L", desc: "Facility diesel & generator fuel", fuelType: "diesel" },
          { name: "Propane (LPG)", unit: "L", factor: 0.00151, factorUnit: "tCO₂e/L", desc: "Propane gas cylinders & bulk" },
          { name: "Wood Pellets", unit: "kg", factor: 0.000045, factorUnit: "tCO₂e/kg", desc: "Biomass heating pellets" },
        ],
      },
      {
        id: "mobile_sources",
        name: "Mobile sources",
        label: "Mobile Sources",
        desc: "Fleet trucks, tractors, trailers & company cars",
        icon: <LuTruck size={20} color="#f97316" />,
        accent: "#f97316",
        categoryType: "utility",
        subtypes: [
          { name: "Diesel", unit: "L", factor: 0.00268, factorUnit: "tCO₂e/L", desc: "Fleet road diesel (2.68 kg CO₂e/L)", fuelType: "diesel" },
          { name: "IFTA Fleet Diesel", unit: "L", factor: 0.00268, factorUnit: "tCO₂e/L", desc: "IFTA quarterly fuel reporting in Litres (auto-converts US gallons: 1 gal = 3.78541 L)", fuelType: "diesel" },
          { name: "Gasoline", unit: "L", factor: 0.00231, factorUnit: "tCO₂e/L", desc: "Light commercial & passenger vehicles" },
          { name: "Biodiesel (B20)", unit: "L", factor: 0.00214, factorUnit: "tCO₂e/L", desc: "20% blend biodiesel" },
          { name: "CNG", unit: "kg", factor: 0.00216, factorUnit: "tCO₂e/kg", desc: "Compressed natural gas" },
        ],
      },
      {
        id: "refrigeration_and_fire_suppression",
        name: "Refrigeration and fire suppression",
        label: "Refrigeration & Fire",
        desc: "HVAC chillers, reefer units & fire extinguishers",
        icon: <LuSnowflake size={20} color="#06b6d4" />,
        accent: "#06b6d4",
        categoryType: "utility",
        subtypes: [
          { name: "R-410A", unit: "kg", factor: 2.088, factorUnit: "tCO₂e/kg", desc: "Commercial HVAC (GWP 2,088)" },
          { name: "R-134a", unit: "kg", factor: 1.430, factorUnit: "tCO₂e/kg", desc: "Reefer chillers & mobile AC (GWP 1,430)" },
          { name: "R-404A", unit: "kg", factor: 3.922, factorUnit: "tCO₂e/kg", desc: "Cold storage transport (GWP 3,922)" },
          { name: "Fire CO2", unit: "kg", factor: 0.001, factorUnit: "tCO₂e/kg", desc: "CO2 fire suppression (GWP 1)" },
          { name: "FM-200", unit: "kg", factor: 3.220, factorUnit: "tCO₂e/kg", desc: "Clean agent (GWP 3,220)" },
        ],
      },
      {
        id: "purchased_gases",
        name: "Purchased gases",
        label: "Purchased Gases",
        desc: "Industrial process gases released during operations",
        icon: <LuFlame size={20} color="#8b5cf6" />,
        accent: "#8b5cf6",
        categoryType: "utility",
        subtypes: [
          { name: "Carbon Dioxide (CO2)", unit: "kg", factor: 0.001, factorUnit: "tCO₂e/kg", desc: "Bulk CO2 gas" },
          { name: "Acetylene", unit: "kg", factor: 0.003, factorUnit: "tCO₂e/kg", desc: "Industrial acetylene" },
        ],
      },
    ],
  },
  "Scope 2": {
    label: "Scope 2 Energy & Electricity",
    documentNoun: "Scope 2 Utility Bills",
    categories: [
      {
        id: "electricity",
        name: "Electricity",
        label: "Purchased Electricity",
        desc: "Grid electricity consumed for facilities & equipment",
        icon: <LuZap size={20} color="#3b82f6" />,
        accent: "#3b82f6",
        categoryType: "utility",
        subtypes: [
          { name: "Location-Based (Canada Average)", unit: "kWh", factor: 0.000120, factorUnit: "tCO₂e/kWh", desc: "Canada national grid average (120 g CO₂e/kWh - NIR)" },
          { name: "Location-Based (Canada Average - DB)", unit: "kWh", factor: 0.000224, factorUnit: "tCO₂e/kWh", desc: "Canada average historical DB factor (224 g CO₂e/kWh)" },
          { name: "Location-Based (Ontario Grid)", unit: "kWh", factor: 0.000030, factorUnit: "tCO₂e/kWh", desc: "Ontario grid factor (30 g CO₂e/kWh)" },
          { name: "Location-Based (Alberta Grid)", unit: "kWh", factor: 0.000540, factorUnit: "tCO₂e/kWh", desc: "Alberta provincial grid average" },
          { name: "Location-Based (British Columbia)", unit: "kWh", factor: 0.000019, factorUnit: "tCO₂e/kWh", desc: "BC hydro clean grid (19 g CO₂e/kWh)" },
          { name: "Location-Based (Quebec Grid)", unit: "kWh", factor: 0.000035, factorUnit: "tCO₂e/kWh", desc: "Quebec hydro grid (35 g CO₂e/kWh)" },
          { name: "Location-Based (US EPA eGRID)", unit: "kWh", factor: 0.000386, factorUnit: "tCO₂e/kWh", desc: "US National Average grid factor" },
          { name: "Market-Based (Green Tariff / REC)", unit: "kWh", factor: 0.000000, factorUnit: "tCO₂e/kWh", desc: "Contractually bundled zero-emission power" },
        ],
      },
      {
        id: "steam",
        name: "Steam",
        label: "Steam & District Energy",
        desc: "Purchased district steam, hot water or cooling",
        icon: <LuWaves size={20} color="#0284c7" />,
        accent: "#0284c7",
        categoryType: "utility",
        subtypes: [
          { name: "District Steam", unit: "kWh", factor: 0.000075, factorUnit: "tCO₂e/kWh", desc: "Natural gas fired steam" },
          { name: "District Hot Water", unit: "GJ", factor: 0.0208, factorUnit: "tCO₂e/GJ", desc: "District heating water" },
        ],
      },
    ],
  },
  "Scope 3": {
    label: "Scope 3 Value Chain Activities",
    documentNoun: "Scope 3 Invoices & Waybills",
    categories: [
      {
        id: "purchased_goods",
        name: "1. Purchased goods and services",
        label: "Purchased Goods & Services",
        desc: "Raw materials, components, packaging & supplier invoices",
        icon: <LuPackage size={20} color="#10b981" />,
        accent: "#10b981",
        categoryType: "goods",
        subtypes: [
          { name: "Virgin Steel", unit: "tonnes", factor: 1.8000, factorUnit: "tCO₂e/tonne", desc: "Primary virgin steel" },
          { name: "Aluminum", unit: "tonnes", factor: 8.2400, factorUnit: "tCO₂e/tonne", desc: "Extruded & rolled aluminum" },
          { name: "Cardboard Packaging", unit: "tonnes", factor: 0.8300, factorUnit: "tCO₂e/tonne", desc: "Corrugated boxes & packaging" },
          { name: "Spend-Based Procurement", unit: "USD", factor: 0.000350, factorUnit: "tCO₂e/$", desc: "EEIO average supplier spend factor" },
        ],
      },
      {
        id: "transportation",
        name: "4. Upstream transportation and distribution",
        label: "Upstream Transportation (Freight)",
        desc: "Third-party logistics, freight waybills & courier invoices",
        icon: <LuTruck size={20} color="#f59e0b" />,
        accent: "#f59e0b",
        categoryType: "freight",
        subtypes: [
          { name: "Road Freight (HGV)", unit: "tkm", factor: 0.000108, factorUnit: "tCO₂e/tkm", desc: "Heavy commercial truck freight" },
          { name: "Air Cargo", unit: "tkm", factor: 0.000602, factorUnit: "tCO₂e/tkm", desc: "Commercial freight cargo" },
          { name: "Rail Freight", unit: "tkm", factor: 0.000028, factorUnit: "tCO₂e/tkm", desc: "Diesel freight train" },
          { name: "Ocean Marine", unit: "tkm", factor: 0.000016, factorUnit: "tCO₂e/tkm", desc: "Container cargo vessel" },
        ],
      },
      {
        id: "waste",
        name: "5. Waste generated in operations",
        label: "Waste Generated in Operations",
        desc: "Waste hauling tickets, landfill manifests & recycling slips",
        icon: <LuTrash2 size={20} color="#8b5cf6" />,
        accent: "#8b5cf6",
        categoryType: "waste",
        subtypes: [
          { name: "Mixed Waste to Landfill", unit: "kg", factor: 0.000450, factorUnit: "tCO₂e/kg", desc: "Landfilled commercial waste" },
          { name: "Recycled Materials", unit: "kg", factor: 0.000021, factorUnit: "tCO₂e/kg", desc: "Cardboard, plastics, metals recycled" },
          { name: "Composted Organics", unit: "kg", factor: 0.000010, factorUnit: "tCO₂e/kg", desc: "Commercial organic composting" },
        ],
      },
      {
        id: "travel",
        name: "6. Business travel",
        label: "Business Travel",
        desc: "Airline tickets, hotel stays & commercial passenger transport",
        icon: <LuPlane size={20} color="#06b6d4" />,
        accent: "#06b6d4",
        categoryType: "travel",
        subtypes: [
          { name: "Commercial Flights", unit: "pkm", factor: 0.000133, factorUnit: "tCO₂e/pkm", desc: "Commercial flights (DEFRA average)" },
          { name: "Hotel Stays", unit: "nights", factor: 0.0280, factorUnit: "tCO₂e/night", desc: "Commercial hotel room night" },
        ],
      },
      {
        id: "fuel_energy",
        name: "3. Fuel and energy-related activities",
        label: "Fuel- & Energy-Related Activities",
        desc: "Well-to-Tank (WTT) upstream fuel extraction & grid T&D losses",
        icon: <LuRepeat size={20} color="#6366f1" />,
        accent: "#6366f1",
        categoryType: "fuel_energy",
        isAutoDerive: true,
        subtypes: [
          { name: "Scope 1 & 2 Auto-Derivation", unit: "Derived", factor: 1.0, factorUnit: "Activity Factors", desc: "Derived from Scope 1 & 2 baseline" },
        ],
      },
    ],
  },
};

export default function EvidenceBillBatchAnalyzerModal({
  open,
  onClose,
  scope = "Scope 1",
  existingFiles = [],
  selectedRow,
  organizationID,
  organizationType,
  pageType = "organization",
  onSuccess,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { enqueueAlert, setRefresh } = useContextProvider();

  // Determine initial scope from selectedRow or prop
  const initialScope = useMemo(() => {
    if (selectedRow) {
      const cat = typeof selectedRow.category === "object" ? selectedRow.category?.name : selectedRow.category;
      if (cat) {
        const lower = String(cat).toLowerCase();
        if (lower.includes("electric") || lower.includes("steam") || lower.includes("scope 2")) return "Scope 2";
        if (
          lower.includes("purchased goods") ||
          lower.includes("capital") ||
          lower.includes("transport") ||
          lower.includes("waste") ||
          lower.includes("travel") ||
          lower.includes("commuting") ||
          lower.includes("scope 3")
        )
          return "Scope 3";
      }
      return "Scope 1";
    }
    const s = String(scope || "").toLowerCase();
    if (s.includes("scope 2") || s.includes("electric")) return "Scope 2";
    if (s.includes("scope 3") || s.includes("waste") || s.includes("freight") || s.includes("travel") || s.includes("goods"))
      return "Scope 3";
    return "Scope 1";
  }, [selectedRow, scope]);

  // State
  const [activeScope, setActiveScope] = useState(initialScope);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubtype, setActiveSubtype] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [progressStatusText, setProgressStatusText] = useState("");
  const [extractedBills, setExtractedBills] = useState([]);
  const [originalExtractedBills, setOriginalExtractedBills] = useState([]);
  const [quarterlyMode, setQuarterlyMode] = useState("unified"); // "unified" or "monthly"
  const [globalFactor, setGlobalFactor] = useState(0.00193);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setActiveScope(initialScope);
      setExtractedBills([]);
      setSelectedFiles([]);
      setQuarterlyMode("unified");
    }
  }, [open, initialScope]);

  // Synchronize activeCategory and activeSubtype when activeScope or selectedRow changes
  useEffect(() => {
    const scopeData = SCOPE_DEFINITIONS[activeScope] || SCOPE_DEFINITIONS["Scope 1"];
    let matchedCat = null;
    if (selectedRow?.category) {
      const rowCatStr = (
        typeof selectedRow.category === "object"
          ? selectedRow.category?.name || selectedRow.category?.label || ""
          : selectedRow.category || ""
      ).toLowerCase().trim();
      matchedCat = scopeData.categories.find(
        (c) =>
          c.name.toLowerCase() === rowCatStr ||
          c.label.toLowerCase() === rowCatStr ||
          rowCatStr.includes(c.id) ||
          c.id.includes(rowCatStr)
      );
    }
    const cat = matchedCat || scopeData.categories[0];
    setActiveCategory(cat);
    const sub = cat?.subtypes?.[0] || null;
    setActiveSubtype(sub);
    if (sub?.factor != null) {
      setGlobalFactor(sub.factor);
    }
  }, [activeScope, selectedRow]);

  // Derived scope meta based on active selection
  const scopeMeta = useMemo(() => {
    const scopeData = SCOPE_DEFINITIONS[activeScope] || SCOPE_DEFINITIONS["Scope 1"];
    const cat = activeCategory || scopeData.categories[0];
    const sub = activeSubtype || cat?.subtypes?.[0] || {};
    const catType = cat?.categoryType || "utility";

    return {
      categoryType: catType,
      subType: sub.name || "",
      icon: cat?.icon || <LuFlame size={22} color="#ef4444" />,
      unit: sub.unit || "m³",
      defaultFactor: sub.factor != null ? sub.factor : 0.00193,
      factorUnit: sub.factorUnit || "tCO₂e/m³",
      label: cat?.label || cat?.name || activeScope,
      documentNoun: scopeData.documentNoun || "Bills & Invoices",
      factorExplanation: `${sub.factor || 0} ${sub.factorUnit || ""} (${sub.desc || cat?.desc || "Standard benchmark factor"}).`,
      isAutoDerive: Boolean(cat?.isAutoDerive),
    };
  }, [activeScope, activeCategory, activeSubtype]);

  // Reset dialog state on open
  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
      setIsAnalyzing(false);
      setIsSaving(false);
      setAnalysisProgress(0);
      setExtractedBills([]);
      setOriginalExtractedBills([]);
      setQuarterlyMode("unified");
      if (scopeMeta.defaultFactor != null) {
        setGlobalFactor(scopeMeta.defaultFactor);
      }
      setDragActive(false);
    }
  }, [open, scopeMeta.defaultFactor]);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = Array.from(e.dataTransfer.files).filter((f) => f.type === "application/pdf" || f.name.endsWith(".pdf"));
      if (dropped.length > 0) {
        setSelectedFiles((prev) => [...prev, ...dropped]);
      }
    }
  }, []);

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = null;
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // Run Batch Analysis (supports "all", "new_only", or "existing_only")
  const handleStartAnalysis = async (targetMode = "all") => {
    const hasExisting = (existingFiles || []).length > 0;
    const hasNew = selectedFiles.length > 0;

    const includeExisting = (targetMode === "all" || targetMode === "existing_only") && hasExisting;
    const includeNew = (targetMode === "all" || targetMode === "new_only") && hasNew;

    if (!includeExisting && !includeNew) {
      enqueueAlert({
        message: t("Please select or upload at least one utility bill PDF to analyze"),
        severity: "warning",
      });
      return;
    }

    setIsAnalyzing(true);
    setAnalysisProgress(15);
    setProgressStatusText(
      includeExisting && includeNew
        ? t("Preparing {{count}} bills for consolidated batch extraction...", { count: (existingFiles || []).length + selectedFiles.length })
        : t("Checking PDF text layers & pre-filtering scans...")
    );

    try {
      const formData = new FormData();
      formData.append("scope", activeScope);
      formData.append("category_name", activeCategory?.name || activeCategory?.label || "");
      formData.append("fuel_subtype", activeSubtype?.name || "");
      formData.append("expected_unit", activeSubtype?.unit || scopeMeta.unit || "");
      const resolvedEntityId = organizationID || selectedRow?.structure_id || selectedRow?.organization_id || selectedRow?.supply_chain_id || selectedRow?.corp_id || selectedRow?.id;
      formData.append("rowID", resolvedEntityId || "unknown");

      let idColumnName = "organization_id";
      if (organizationType === "Supply_chain" || pageType === "supply_chain") idColumnName = "supply_chain_id";
      else if (organizationType === "Corporate" || pageType === "corporate") idColumnName = "corporate_id";
      else if (organizationType === "Structure" || pageType === "organization") idColumnName = "organization_id";
      else if (pageType === "valueChain") idColumnName = "product_service_id";
      formData.append("IDColumnName", idColumnName);

      // Pass existing files already stored on the server if targeted
      if (includeExisting) {
        const existingNames = existingFiles.map((f) => (typeof f === "string" ? f : f.name));
        formData.append("existing_file_names", JSON.stringify(existingNames));
      }

      // Append newly selected local files if targeted
      if (includeNew) {
        selectedFiles.forEach((file) => {
          formData.append("files", file);
        });
      }

      // Smooth progress ticks
      const timer1 = setTimeout(() => {
        setAnalysisProgress(45);
        setProgressStatusText(t("Consolidating bills into token-efficient batch prompt..."));
      }, 700);

      const timer2 = setTimeout(() => {
        setAnalysisProgress(75);
        setProgressStatusText(t("Extracting billing periods, meter readings, and consumption..."));
      }, 1600);

      const res = await EvidenceAnalysis.analyzeBillsBatch(formData);
      clearTimeout(timer1);
      clearTimeout(timer2);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || t("Failed to analyze bill batch"));
      }

      const result = await res.json();
      setAnalysisProgress(100);
      setProgressStatusText(t("Batch extraction completed successfully!"));

      const bills = result.bills || [];
      const summary = result.summary || {};

      const factorToUse = activeSubtype?.factor != null ? activeSubtype.factor : (summary.default_factor || scopeMeta.defaultFactor);
      setGlobalFactor(factorToUse);

      const billsWithFactor = bills.map((b) => {
        let u = parseFloat(b.raw_usage) || 0;
        let uUnit = b.unit || activeSubtype?.unit || scopeMeta.unit;

        // 1. Auto-convert IFTA / Fleet Diesel if reported in US Gallons to Litres
        const isDieselMode = activeSubtype?.fuelType === "diesel" || (b.fuel_subtype && b.fuel_subtype.toLowerCase().includes("diesel")) || (activeCategory?.id === "mobile_sources");
        if (isDieselMode && (String(uUnit).toLowerCase().includes("gal") || String(uUnit).toLowerCase() === "gallon")) {
          u = parseFloat((u * 3.78541).toFixed(2));
          uUnit = "L";
        }

        // 2. Auto-convert Natural Gas between m³ and kWh
        const isGasMode = activeSubtype?.fuelType === "natural_gas" || (b.fuel_subtype && b.fuel_subtype.toLowerCase().includes("gas")) || (activeCategory?.id === "stationary_combustion");
        if (isGasMode) {
          if ((activeSubtype?.unit === "kWh" || activeSubtype?.name?.includes("kWh")) && (String(uUnit).toLowerCase().includes("m3") || String(uUnit).toLowerCase().includes("m³"))) {
            u = parseFloat((u * 10.55).toFixed(2));
            uUnit = "kWh";
          } else if ((activeSubtype?.unit === "m³" || activeSubtype?.unit === "m3") && String(uUnit).toLowerCase() === "kwh") {
            u = parseFloat((u / 10.55).toFixed(2));
            uUnit = "m³";
          }
        }

        // 3. Auto-convert Electricity MWh to kWh
        if (String(uUnit).toLowerCase() === "mwh" && (activeCategory?.id === "electricity" || scopeMeta.unit === "kWh")) {
          u = parseFloat((u * 1000).toFixed(2));
          uUnit = "kWh";
        }

        return {
          ...b,
          raw_usage: u,
          unit: uUnit,
          factor: factorToUse,
          tco2e: parseFloat((u * factorToUse).toFixed(4)),
        };
      });

      setOriginalExtractedBills(billsWithFactor);
      setExtractedBills(billsWithFactor);
      setQuarterlyMode("unified");

      enqueueAlert({
        message: t("Successfully extracted {{count}} bills in consolidated batch", { count: bills.length }),
        severity: "success",
      });
    } catch (err) {
      console.error("Batch bill extraction error:", err);
      enqueueAlert({
        message: err.message || t("Error occurred during bill analysis"),
        severity: "error",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Live Unit Toggle for Natural Gas (m³ ↔ kWh at 1 m³ = 10.55 kWh)
  const handleNaturalGasUnitToggle = (targetUnit) => {
    if (!targetUnit || targetUnit === scopeMeta.unit) return;
    const cat = activeCategory || SCOPE_DEFINITIONS["Scope 1"].categories[0];
    const targetSub = cat?.subtypes?.find((s) => s.unit === targetUnit) || {
      name: targetUnit === "kWh" ? "Natural Gas (kWh)" : "Natural Gas (m³)",
      unit: targetUnit,
      factor: targetUnit === "kWh" ? 0.000183 : 0.00193,
      factorUnit: targetUnit === "kWh" ? "tCO₂e/kWh" : "tCO₂e/m³",
      fuelType: "natural_gas",
      desc: targetUnit === "kWh" ? "Natural gas energy equivalent (1 m³ = 10.55 kWh)" : "Pipeline gas per m³ (Canada NIR 1.93 kg/m³)",
    };

    setActiveSubtype(targetSub);
    setGlobalFactor(targetSub.factor);

    setExtractedBills((prev) =>
      prev.map((b) => {
        const curUsage = parseFloat(b.raw_usage) || 0;
        let newUsage = curUsage;
        if (targetUnit === "kWh" && (b.unit === "m³" || b.unit === "m3")) {
          newUsage = parseFloat((curUsage * 10.55).toFixed(2));
        } else if ((targetUnit === "m³" || targetUnit === "m3") && b.unit === "kWh") {
          newUsage = parseFloat((curUsage / 10.55).toFixed(2));
        }
        return {
          ...b,
          raw_usage: newUsage,
          unit: targetUnit,
          factor: targetSub.factor,
          tco2e: parseFloat((newUsage * targetSub.factor).toFixed(4)),
        };
      })
    );
  };

  // Live Unit Toggle for Fleet Diesel (L ↔ gal at 1 US gal = 3.78541 L)
  const handleDieselUnitToggle = (targetUnit) => {
    if (!targetUnit || targetUnit === scopeMeta.unit) return;
    const isGal = targetUnit === "gal";
    const targetSub = {
      name: isGal ? "IFTA Diesel (Gallons)" : "IFTA Fleet Diesel",
      unit: targetUnit,
      factor: isGal ? 0.01014 : 0.00268,
      factorUnit: isGal ? "tCO₂e/gal" : "tCO₂e/L",
      fuelType: "diesel",
      desc: isGal ? "Fleet diesel per US Gallon (1 gal = 3.78541 L)" : "Fleet road diesel in Litres",
    };

    setActiveSubtype(targetSub);
    setGlobalFactor(targetSub.factor);

    setExtractedBills((prev) =>
      prev.map((b) => {
        const curUsage = parseFloat(b.raw_usage) || 0;
        let newUsage = curUsage;
        if (targetUnit === "L" && (b.unit === "gal" || b.unit === "gallon")) {
          newUsage = parseFloat((curUsage * 3.78541).toFixed(2));
        } else if (targetUnit === "gal" && b.unit === "L") {
          newUsage = parseFloat((curUsage / 3.78541).toFixed(2));
        }
        return {
          ...b,
          raw_usage: newUsage,
          unit: targetUnit,
          factor: targetSub.factor,
          tco2e: parseFloat((newUsage * targetSub.factor).toFixed(4)),
        };
      })
    );
  };

  const handleSubtypeSelect = (sub) => {
    setActiveSubtype(sub);
    if (sub?.factor != null) {
      setGlobalFactor(sub.factor);
      handleGlobalFactorChange(sub.factor);
    }
  };

  const hasQuarterlyBills = useMemo(() => {
    return originalExtractedBills.some((b) => {
      const months = parseInt(b.months_covered, 10) || 1;
      const isQ = Boolean(b.is_quarterly);
      const mStr = (b.billing_month || "").toLowerCase();
      return (
        months > 1 ||
        isQ ||
        mStr.includes("q1") ||
        mStr.includes("q2") ||
        mStr.includes("q3") ||
        mStr.includes("q4") ||
        mStr.includes("quarter") ||
        (mStr.includes("-") && !mStr.match(/^\d{4}-\d{2}-\d{2}$/))
      );
    });
  }, [originalExtractedBills]);

  const handleToggleQuarterlyMode = useCallback((mode) => {
    setQuarterlyMode(mode);
    if (mode === "unified") {
      setExtractedBills(originalExtractedBills);
      return;
    }

    const expanded = [];
    originalExtractedBills.forEach((b) => {
      const months = parseInt(b.months_covered, 10) || (b.is_quarterly ? 3 : 1);
      const mStr = (b.billing_month || "").toLowerCase();
      const isMulti =
        months > 1 ||
        b.is_quarterly ||
        mStr.includes("q1") ||
        mStr.includes("q2") ||
        mStr.includes("q3") ||
        mStr.includes("q4") ||
        mStr.includes("quarter") ||
        (mStr.includes("-") && !mStr.match(/^\d{4}-\d{2}-\d{2}$/));

      if (!isMulti) {
        expanded.push(b);
        return;
      }

      const count = months > 1 ? months : 3;
      const rawU = parseFloat(b.raw_usage) || 0;
      const c = parseFloat(b.cost) || 0;
      const fact = parseFloat(b.factor || globalFactor) || 0;

      const splitUsage = parseFloat((rawU / count).toFixed(2));
      const splitCost = parseFloat((c / count).toFixed(2));
      const splitTco2e = parseFloat((splitUsage * fact).toFixed(4));

      let monthLabels = [];
      if (mStr.includes("q1")) monthLabels = ["January", "February", "March"];
      else if (mStr.includes("q2")) monthLabels = ["April", "May", "June"];
      else if (mStr.includes("q3")) monthLabels = ["July", "August", "September"];
      else if (mStr.includes("q4")) monthLabels = ["October", "November", "December"];
      else {
        monthLabels = Array.from({ length: count }, (_, i) => `Month ${i + 1}`);
      }

      const yearMatch = (b.billing_month || "").match(/\b(20\d\d)\b/);
      const yearStr = yearMatch ? ` ${yearMatch[1]}` : "";

      for (let i = 0; i < count; i++) {
        expanded.push({
          ...b,
          bill_id: `${b.bill_id || 1}.${i + 1}`,
          billing_month: `${monthLabels[i] || `Period Part ${i + 1}`}${yearStr}`,
          raw_usage: i === count - 1 ? parseFloat((rawU - splitUsage * (count - 1)).toFixed(2)) : splitUsage,
          cost: i === count - 1 ? parseFloat((c - splitCost * (count - 1)).toFixed(2)) : splitCost,
          tco2e: splitTco2e,
          notes: `${b.notes ? b.notes + " | " : ""}Split from ${b.billing_month} (1/${count} pro-rata)`,
          _isSplitMonth: true,
        });
      }
    });

    setExtractedBills(expanded);
  }, [originalExtractedBills, globalFactor]);

  // Run Category 3 Mathematical Auto-Derivation from Scope 1 & 2 baseline
  const handleAutoDeriveCategory3 = async () => {
    setIsAnalyzing(true);
    setAnalysisProgress(20);
    setProgressStatusText(t("Querying Scope 1 and Scope 2 baseline emissions..."));

    try {
      let idColumnName = "structure_id";
      if (pageType === "valueChain") idColumnName = "product_service_id";
      else if (pageType === "organization") idColumnName = "organization_id";
      else if (pageType === "supply_chain") idColumnName = "supply_chain_id";
      else if (pageType === "corporate") idColumnName = "corporate_id";

      const timer1 = setTimeout(() => {
        setAnalysisProgress(60);
        setProgressStatusText(t("Computing Upstream Well-to-Tank (WTT) & Grid T&D losses..."));
      }, 500);

      const res = await EvidenceAnalysis.autoDeriveCategory3({
        rowID: selectedRow?.id || "unknown",
        IDColumnName: idColumnName,
      });
      clearTimeout(timer1);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || t("Failed to auto-derive Category 3 emissions"));
      }

      const result = await res.json();
      setAnalysisProgress(100);
      setProgressStatusText(t("Category 3 activity derivation completed!"));

      const bills = result.bills || [];
      const summary = result.summary || {};

      if (!result.scope1_found && !result.scope2_found) {
        enqueueAlert({
          message: summary.message || t("No Scope 1 or Scope 2 baseline emissions found for this facility. Please analyze and commit Scope 1 (Gas) or Scope 2 (Electricity) utility bills first."),
          severity: "warning",
          duration: 7000,
        });
        return;
      }

      setExtractedBills(bills);

      enqueueAlert({
        message: t("Successfully auto-derived {{count}} Category 3 activity components from Scope 1 & 2", { count: bills.length }),
        severity: "success",
      });
    } catch (err) {
      console.error("Auto-derive Category 3 error:", err);
      enqueueAlert({
        message: err.message || t("Error occurred during Category 3 auto-derivation"),
        severity: "error",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Recalculate all rows if global factor changes
  const handleGlobalFactorChange = (newFactorVal) => {
    const parsed = parseFloat(newFactorVal) || 0;
    setGlobalFactor(parsed);
    setExtractedBills((prev) =>
      prev.map((b) => {
        const usage = parseFloat(b.raw_usage) || 0;
        return {
          ...b,
          factor: parsed,
          tco2e: parseFloat((usage * parsed).toFixed(4)),
        };
      })
    );
  };

  // Live aggregated totals
  const liveTotals = useMemo(() => {
    let totalUsage = 0;
    let totalCost = 0;
    let totalTco2e = 0;
    let validCount = 0;

    extractedBills.forEach((b) => {
      const u = parseFloat(b.raw_usage) || 0;
      const c = parseFloat(b.cost) || 0;
      const t = parseFloat(b.tco2e) || 0;
      totalUsage += u;
      totalCost += c;
      totalTco2e += t;
      if (u > 0 || c > 0 || t > 0) validCount++;
    });

    return {
      totalUsage: totalUsage.toFixed(2),
      totalCost: totalCost.toFixed(2),
      totalTco2e: totalTco2e.toFixed(4),
      validCount,
      currency: extractedBills.find((b) => b.currency)?.currency || selectedRow?.currency || "",
      unit: extractedBills[0]?.unit || scopeMeta.unit,
    };
  }, [extractedBills, scopeMeta, selectedRow]);

  // AgGrid inline cell editing handler
  const handleCellValueChanged = useCallback((params) => {
    const updated = { ...params.data };
    const rowIndex = params.node.rowIndex;

    // Freight interactive recalculation: tonne-km = cargo tonnes * distance km
    if (scopeMeta.categoryType === "freight") {
      const dist = parseFloat(updated.distance_km) || 0;
      const cargo = parseFloat(updated.cargo_weight_tonnes) || 0;
      if (params.colDef.field === "distance_km" || params.colDef.field === "cargo_weight_tonnes") {
        updated.raw_usage = parseFloat((dist * cargo).toFixed(2));
      }
    }

    const usage = parseFloat(updated.raw_usage) || 0;
    const factor = parseFloat(updated.factor || globalFactor) || 0;
    updated.tco2e = parseFloat((usage * factor).toFixed(4));

    setExtractedBills((prev) => {
      const copy = [...prev];
      copy[rowIndex] = updated;
      return copy;
    });

    if (params.api) {
      params.api.refreshCells({ rowNodes: [params.node], columns: ["raw_usage", "tco2e"] });
    }
  }, [globalFactor, scopeMeta.categoryType]);

  // AgGrid column definitions conforming strictly to project design rules
  const columnDefs = useMemo(() => {
    const cat = scopeMeta.categoryType;

    // Common column: #
    const indexCol = {
      headerName: "#",
      valueGetter: (params) => (params.node.rowIndex != null ? params.node.rowIndex + 1 : ""),
      width: 55,
      maxWidth: 60,
      ...centerAlignColDef,
      pinned: "left",
    };

    // Common column: Status
    const statusCol = {
      headerName: t("Status"),
      field: "status",
      minWidth: 125,
      maxWidth: 145,
      ...centerAlignColDef,
      cellRenderer: (params) => {
        const isScan = params.value === "unreadable_scan";
        return isScan ? (
          <M3Badge tone="warning" size="sm">
            {t("Scan / Manual")}
          </M3Badge>
        ) : (
          <M3Badge tone="success" size="sm" icon={<CheckCircleOutlineIcon style={{ fontSize: 13 }} />}>
            {t("Verified")}
          </M3Badge>
        );
      },
    };

    // Common cell renderers for graceful empty date handling & dynamic currency
    const renderPeriodCell = (params) => {
      const val = params.value;
      if (!val || val === "N/A" || val === "None" || val === "null" || val === "undefined") {
        return (
          <span style={{ color: "var(--m3-outline, #94a3b8)", fontSize: "12px", fontStyle: "italic", fontWeight: 400 }}>
            {t("No date data available")}
          </span>
        );
      }
      const periodType = params.data?.period_type || (params.data?.is_quarterly ? "Quarterly" : null);
      const isQ = periodType && String(periodType).toLowerCase().includes("quarter");
      const isA = periodType && String(periodType).toLowerCase().includes("annual");

      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
          <span>{val}</span>
          {isQ && (
            <span
              style={{
                padding: "1px 6px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: 600,
                backgroundColor: "rgba(99, 102, 241, 0.12)",
                color: "#4f46e5",
              }}
            >
              {t("Quarterly")}
            </span>
          )}
          {isA && (
            <span
              style={{
                padding: "1px 6px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: 600,
                backgroundColor: "rgba(168, 85, 247, 0.12)",
                color: "#7e22ce",
              }}
            >
              {t("Annual")}
            </span>
          )}
        </div>
      );
    };

    const costValueFormatter = (params) => {
      if (params.value == null || params.value === "") return "-";
      const val = Number(params.value);
      if (isNaN(val)) return "-";
      const curr = params.data?.currency || liveTotals.currency;
      const formatted = val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return curr ? `${formatted} ${curr}` : formatted;
    };

    // 1. Category 5: Waste Generated in Operations
    if (cat === "waste") {
      return [
        indexCol,
        {
          headerName: t("Invoice File"),
          field: "file_name",
          minWidth: 180,
          flex: 1.2,
          ...leftAlignColDef,
          cellRenderer: (params) => (
            <Tooltip title={params.value || ""}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <LuTrash2 size={16} color="#8b5cf6" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {params.value}
                </span>
              </div>
            </Tooltip>
          ),
        },
        {
          headerName: t("Period / Date"),
          field: "billing_month",
          minWidth: 140,
          flex: 1,
          editable: true,
          ...centerAlignColDef,
          cellStyle: { ...centerAlignColDef.cellStyle, fontWeight: 600 },
          cellRenderer: renderPeriodCell,
        },
        {
          headerName: t("Waste Stream / Type"),
          field: "waste_type",
          minWidth: 160,
          flex: 1.1,
          editable: true,
          ...leftAlignColDef,
        },
        {
          headerName: t("Disposal Route"),
          field: "disposal_route",
          minWidth: 140,
          flex: 1,
          editable: true,
          ...centerAlignColDef,
        },
        {
          headerName: `${t("Weight")} (kg)`,
          field: "raw_usage",
          minWidth: 125,
          flex: 0.9,
          editable: true,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 600 },
          valueFormatter: (params) =>
            params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 }) : "0.0",
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: liveTotals.currency ? `${t("Cost")} (${liveTotals.currency})` : t("Cost"),
          field: "cost",
          minWidth: 110,
          flex: 0.8,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: costValueFormatter,
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Factor")} (tCO₂e/kg)`,
          field: "factor",
          minWidth: 130,
          flex: 0.85,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(6) : "0.000000"),
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Emissions")} (tCO₂e)`,
          field: "tco2e",
          minWidth: 135,
          flex: 0.9,
          editable: false,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, color: "#659d33", fontWeight: 700 },
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(4) : "0.0000"),
        },
        statusCol,
      ];
    }

    // 2. Category 4: Upstream Transportation & Distribution (Freight)
    if (cat === "freight") {
      return [
        indexCol,
        {
          headerName: t("Waybill File"),
          field: "file_name",
          minWidth: 180,
          flex: 1.2,
          ...leftAlignColDef,
          cellRenderer: (params) => (
            <Tooltip title={params.value || ""}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <LuTruck size={16} color="#f59e0b" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {params.value}
                </span>
              </div>
            </Tooltip>
          ),
        },
        {
          headerName: t("Period / Date"),
          field: "billing_month",
          minWidth: 140,
          flex: 0.95,
          editable: true,
          ...centerAlignColDef,
          cellStyle: { ...centerAlignColDef.cellStyle, fontWeight: 600 },
          cellRenderer: renderPeriodCell,
        },
        {
          headerName: t("Transport Mode"),
          field: "vehicle_mode",
          minWidth: 150,
          flex: 1,
          editable: true,
          ...leftAlignColDef,
        },
        {
          headerName: `${t("Distance")} (km)`,
          field: "distance_km",
          minWidth: 120,
          flex: 0.85,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: (params) =>
            params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "0.0",
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Cargo")} (t)`,
          field: "cargo_weight_tonnes",
          minWidth: 110,
          flex: 0.8,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: (params) =>
            params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Activity")} (tkm)`,
          field: "raw_usage",
          minWidth: 130,
          flex: 0.9,
          editable: true,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 600 },
          valueFormatter: (params) =>
            params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "0.0",
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: liveTotals.currency ? `${t("Cost")} (${liveTotals.currency})` : t("Cost"),
          field: "cost",
          minWidth: 110,
          flex: 0.8,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: costValueFormatter,
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Emissions")} (tCO₂e)`,
          field: "tco2e",
          minWidth: 135,
          flex: 0.9,
          editable: false,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, color: "#659d33", fontWeight: 700 },
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(4) : "0.0000"),
        },
        statusCol,
      ];
    }

    // 3. Category 6: Business Travel
    if (cat === "travel") {
      return [
        indexCol,
        {
          headerName: t("Ticket / Folio File"),
          field: "file_name",
          minWidth: 180,
          flex: 1.2,
          ...leftAlignColDef,
          cellRenderer: (params) => (
            <Tooltip title={params.value || ""}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <LuPlane size={16} color="#06b6d4" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {params.value}
                </span>
              </div>
            </Tooltip>
          ),
        },
        {
          headerName: t("Date / Period"),
          field: "billing_month",
          minWidth: 140,
          flex: 1,
          editable: true,
          ...centerAlignColDef,
          cellStyle: { ...centerAlignColDef.cellStyle, fontWeight: 600 },
          cellRenderer: renderPeriodCell,
        },
        {
          headerName: t("Travel Type / Mode"),
          field: "travel_type",
          minWidth: 160,
          flex: 1.1,
          editable: true,
          ...leftAlignColDef,
        },
        {
          headerName: t("Activity Units"),
          field: "raw_usage",
          minWidth: 130,
          flex: 0.9,
          editable: true,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 600 },
          valueFormatter: (params) => {
            const unit = params.data?.unit || "";
            return params.value != null ? `${Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${unit}` : "0.0";
          },
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: liveTotals.currency ? `${t("Cost")} (${liveTotals.currency})` : t("Cost"),
          field: "cost",
          minWidth: 110,
          flex: 0.8,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: costValueFormatter,
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Factor")}`,
          field: "factor",
          minWidth: 120,
          flex: 0.8,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(6) : "0.000000"),
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Emissions")} (tCO₂e)`,
          field: "tco2e",
          minWidth: 135,
          flex: 0.9,
          editable: false,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, color: "#659d33", fontWeight: 700 },
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(4) : "0.0000"),
        },
        statusCol,
      ];
    }

    // 4. Category 1: Purchased Goods and Services
    if (cat === "goods") {
      return [
        indexCol,
        {
          headerName: t("Invoice File"),
          field: "file_name",
          minWidth: 180,
          flex: 1.2,
          ...leftAlignColDef,
          cellRenderer: (params) => (
            <Tooltip title={params.value || ""}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <LuPackage size={16} color="#10b981" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {params.value}
                </span>
              </div>
            </Tooltip>
          ),
        },
        {
          headerName: t("Period / Date"),
          field: "billing_month",
          minWidth: 140,
          flex: 0.95,
          editable: true,
          ...centerAlignColDef,
          cellStyle: { ...centerAlignColDef.cellStyle, fontWeight: 600 },
          cellRenderer: renderPeriodCell,
        },
        {
          headerName: t("Supplier"),
          field: "supplier_name",
          minWidth: 150,
          flex: 1,
          editable: true,
          ...leftAlignColDef,
          valueGetter: (params) => params.data?.supplier_name || params.data?.utility_provider || "",
        },
        {
          headerName: t("Material / Item"),
          field: "material_type",
          minWidth: 150,
          flex: 1,
          editable: true,
          ...leftAlignColDef,
        },
        {
          headerName: `${t("Weight")} (tonnes)`,
          field: "raw_usage",
          minWidth: 130,
          flex: 0.9,
          editable: true,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 600 },
          valueFormatter: (params) =>
            params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) : "0.000",
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: liveTotals.currency ? `${t("Spend")} (${liveTotals.currency})` : t("Spend"),
          field: "cost",
          minWidth: 110,
          flex: 0.8,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: costValueFormatter,
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Factor")} (tCO₂e/t)`,
          field: "factor",
          minWidth: 130,
          flex: 0.85,
          editable: true,
          ...rightAlignColDef,
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(4) : "0.0000"),
          valueParser: (params) => parseFloat(params.newValue) || 0,
        },
        {
          headerName: `${t("Emissions")} (tCO₂e)`,
          field: "tco2e",
          minWidth: 135,
          flex: 0.9,
          editable: false,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, color: "#659d33", fontWeight: 700 },
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(4) : "0.0000"),
        },
        statusCol,
      ];
    }

    // 5. Category 3: Fuel- and Energy-Related Activities (Auto-Derived)
    if (cat === "fuel_energy") {
      return [
        indexCol,
        {
          headerName: t("Activity Component"),
          field: "derivation_type",
          minWidth: 260,
          flex: 1.8,
          ...leftAlignColDef,
          cellRenderer: (params) => (
            <Tooltip title={params.data?.notes || ""}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <LuRepeat size={16} color="#6366f1" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--m3-on-surface, #0f172a)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {params.value}
                </span>
              </div>
            </Tooltip>
          ),
        },
        {
          headerName: t("Source Inventory"),
          field: "file_name",
          minWidth: 190,
          flex: 1.2,
          ...leftAlignColDef,
        },
        {
          headerName: t("Scope / Period"),
          field: "billing_month",
          minWidth: 150,
          flex: 1,
          ...centerAlignColDef,
          cellRenderer: renderPeriodCell,
        },
        {
          headerName: t("Base Quantity"),
          field: "raw_usage",
          minWidth: 140,
          flex: 1,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 600 },
          valueFormatter: (params) => {
            const unit = params.data?.unit || "";
            return params.value != null ? `${Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}` : "0.00";
          },
        },
        {
          headerName: t("Factor / Ratio"),
          field: "factor",
          minWidth: 140,
          flex: 1,
          ...rightAlignColDef,
          valueFormatter: (params) => {
            const unit = params.data?.factor_unit || "";
            return params.value != null ? `${Number(params.value).toFixed(6)} ${unit}` : "-";
          },
        },
        {
          headerName: `${t("Emissions")} (tCO₂e)`,
          field: "tco2e",
          minWidth: 140,
          flex: 1,
          editable: false,
          ...rightAlignColDef,
          cellStyle: { ...rightAlignColDef.cellStyle, color: "#659d33", fontWeight: 700 },
          valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(4) : "0.0000"),
        },
        statusCol,
      ];
    }

    // 6. Default: Utility bills (Scope 1 & 2)
    return [
      indexCol,
      {
        headerName: t("Bill File"),
        field: "file_name",
        minWidth: 180,
        flex: 1.2,
        ...leftAlignColDef,
        cellRenderer: (params) => (
          <Tooltip title={params.value || ""}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
              <LuFileText size={16} color="#d97706" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {params.value}
              </span>
            </div>
          </Tooltip>
        ),
      },
      {
        headerName: t("Billing Period"),
        field: "billing_month",
        minWidth: 160,
        flex: 1.1,
        editable: true,
        ...centerAlignColDef,
        cellStyle: {
          ...centerAlignColDef.cellStyle,
          fontWeight: 600,
          color: "var(--m3-on-surface, #0f172a)",
        },
        cellRenderer: renderPeriodCell,
      },
      {
        headerName: t("Period Start"),
        field: "billing_period_start",
        minWidth: 130,
        flex: 0.9,
        editable: true,
        ...centerAlignColDef,
        cellRenderer: (params) => {
          if (!params.value || params.value === "N/A" || params.value === "None") {
            return <span style={{ color: "var(--m3-outline, #94a3b8)", fontSize: "12px", fontStyle: "italic" }}>{t("No date data available")}</span>;
          }
          return params.value;
        },
      },
      {
        headerName: t("Period End"),
        field: "billing_period_end",
        minWidth: 130,
        flex: 0.9,
        editable: true,
        ...centerAlignColDef,
        cellRenderer: (params) => {
          if (!params.value || params.value === "N/A" || params.value === "None") {
            return <span style={{ color: "var(--m3-outline, #94a3b8)", fontSize: "12px", fontStyle: "italic" }}>-</span>;
          }
          return params.value;
        },
      },
      {
        headerName: t("Meter Number"),
        field: "meter_number",
        minWidth: 135,
        flex: 0.9,
        editable: true,
        ...leftAlignColDef,
      },
      {
        headerName: t("Utility Provider"),
        field: "utility_provider",
        minWidth: 145,
        flex: 1,
        editable: true,
        ...leftAlignColDef,
      },
      {
        headerName: `${t("Usage")} (${liveTotals.unit})`,
        field: "raw_usage",
        minWidth: 130,
        flex: 0.9,
        editable: true,
        ...rightAlignColDef,
        cellStyle: {
          ...rightAlignColDef.cellStyle,
          fontWeight: 600,
        },
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
        valueParser: (params) => parseFloat(params.newValue) || 0,
      },
      {
        headerName: liveTotals.currency ? `${t("Cost")} (${liveTotals.currency})` : t("Cost"),
        field: "cost",
        minWidth: 120,
        flex: 0.85,
        editable: true,
        ...rightAlignColDef,
        valueFormatter: costValueFormatter,
        valueParser: (params) => parseFloat(params.newValue) || 0,
      },
      {
        headerName: t("Currency"),
        field: "currency",
        minWidth: 90,
        flex: 0.6,
        editable: true,
        ...centerAlignColDef,
      },
      {
        headerName: `${t("Emissions")} (tCO₂e)`,
        field: "tco2e",
        minWidth: 145,
        flex: 1,
        editable: false,
        ...rightAlignColDef,
        cellStyle: {
          ...rightAlignColDef.cellStyle,
          color: "#4a7c23",
          fontWeight: 700,
        },
        cellRenderer: (params) => {
          const val = params.value != null ? Number(params.value).toFixed(4) : "0.0000";
          const usage = params.data?.raw_usage != null ? Number(params.data.raw_usage).toLocaleString() : "0";
          const unit = params.data?.unit || liveTotals.unit || "";
          const factor = params.data?.factor != null ? params.data.factor : globalFactor;
          const formulaTooltip = `${usage} ${unit} × ${factor} = ${val} tCO₂e`;
          return (
            <Tooltip title={formulaTooltip} arrow>
              <span style={{ cursor: "default" }}>{val}</span>
            </Tooltip>
          );
        },
      },
      {
        headerName: t("Notes"),
        field: "notes",
        minWidth: 160,
        flex: 1.2,
        editable: true,
        ...leftAlignColDef,
      },
      statusCol,
    ];
  }, [t, scopeMeta.categoryType, liveTotals.unit, liveTotals.currency, globalFactor]);

  // Save reviewed batch to inventory
  const handleSaveToInventory = async () => {
    if (extractedBills.length === 0) return;

    setIsSaving(true);
    try {
      let idColumnName = "organization_id";
      if (organizationType === "Supply_chain" || pageType === "supply_chain") idColumnName = "supply_chain_id";
      else if (organizationType === "Corporate" || pageType === "corporate") idColumnName = "corporate_id";
      else if (organizationType === "Structure" || pageType === "organization") idColumnName = "organization_id";
      else if (pageType === "valueChain") idColumnName = "product_service_id";

      const resolvedEntityId = organizationID || selectedRow?.structure_id || selectedRow?.organization_id || selectedRow?.supply_chain_id || selectedRow?.corp_id || selectedRow?.id;

      const payload = {
        scope: activeScope,
        category: activeCategory?.name || activeCategory?.label || (typeof selectedRow?.category === 'object' ? selectedRow.category?.name : selectedRow?.category) || "Stationary combustion",
        fuel_subtype: activeSubtype?.name || "",
        fuel_type: activeSubtype?.fuelType || "",
        category_id: activeCategory?.id || "",
        parent_emission_id: selectedRow?.id || null,
        rowID: resolvedEntityId,
        IDColumnName: idColumnName,
        bills: extractedBills,
        emission_factor: globalFactor,
        factor_unit: activeSubtype?.factorUnit || scopeMeta.factorUnit,
      };

      const res = await EvidenceAnalysis.saveBatchEmissions(payload);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || t("Failed to save batch emissions"));
      }

      const resData = await res.json();
      enqueueAlert({
        message: t("Batch bills successfully mapped to GHG Inventory ({{count}} records saved)", {
          count: resData.details_count || extractedBills.length,
        }),
        severity: "success",
        duration: 4000,
      });

      if (setRefresh) setRefresh((r) => !r);
      if (onSuccess) onSuccess(resData);
      onClose();
    } catch (err) {
      console.error("Save emissions error:", err);
      enqueueAlert({
        message: err.message || t("Error saving emissions to inventory"),
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasExistingFiles = (existingFiles || []).length > 0;
  const totalStagedCount = (existingFiles || []).length + selectedFiles.length;

  return (
    <Dialog
      open={open}
      onClose={isAnalyzing || isSaving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "var(--m3-radius-lg, 16px)",
          background: isDark ? "rgba(15, 23, 42, 0.98)" : "#ffffff",
          boxShadow: "var(--m3-elevation-3, 0 8px 24px rgba(0,0,0,0.12))",
          border: "1px solid var(--m3-outline-variant, #cbd5e1)",
          maxWidth: "1160px",
          width: "95%",
          overflow: "hidden",
        },
      }}
    >
      {/* Dialog Header — strictly left-aligned like OrganizationDocumentUpload */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          borderBottom: "1px solid var(--m3-outline-variant, #cbd5e1)",
          background: isDark ? "rgba(255,255,255,0.02)" : "var(--m3-surface-container-lowest, #ffffff)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", textAlign: "left" }}>
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: 600,
              color: "var(--m3-on-surface, #0f172a)",
              fontFamily: "var(--m3-font, Poppins, sans-serif)",
              textAlign: "left",
            }}
          >
            {scopeMeta.categoryType === "fuel_energy"
              ? t("Auto-Derive Category 3 Energy & Fuel Activities")
              : t("Upload & Analyze {{label}}", { label: scopeMeta.documentNoun })}
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "var(--m3-on-surface-variant, #64748b)",
              fontFamily: "var(--m3-font, Poppins, sans-serif)",
              textAlign: "left",
            }}
          >
            {activeScope} · {scopeMeta.label}
            {activeSubtype?.name ? ` · ${activeSubtype.name}` : ""}
            {selectedRow ? ` · ${t("Anchored to Inventory Row")}` : ""}
          </p>
        </div>

        <M3IconButton
          size="sm"
          icon={<CloseIcon style={{ fontSize: "18px" }} />}
          variant="standard"
          onClick={onClose}
          tooltip={t("Close")}
          disabled={isAnalyzing || isSaving}
        />
      </div>

      {/* Dialog Body with consistent 20px gap and 24px padding */}
      <div style={{ padding: "24px", maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
        <input
          type="file"
          ref={fileInputRef}
          multiple
          accept={ACCEPTED_FILE_TYPES}
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* SECTION 1: Before Analysis */}
          {extractedBills.length === 0 && !isAnalyzing && (
            <>
              {scopeMeta.categoryType === "fuel_energy" ? (
                /* Category 3 Auto-Derivation Preview Card */
                <div
                  style={{
                    padding: "24px",
                    borderRadius: "var(--m3-radius-lg, 16px)",
                    background: isDark ? "rgba(99, 102, 241, 0.10)" : "rgba(99, 102, 241, 0.05)",
                    border: "1px solid rgba(99, 102, 241, 0.25)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "18px",
                    textAlign: "left",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        background: "rgba(99, 102, 241, 0.15)",
                        color: "#4f46e5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LuRepeat size={26} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "var(--m3-on-surface, #0f172a)",
                          fontFamily: "var(--m3-font, Poppins, sans-serif)",
                          textAlign: "left",
                        }}
                      >
                        {t("Automated Energy & Fuel Activity Derivation")}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          lineHeight: "1.6",
                          color: "var(--m3-on-surface-variant, #64748b)",
                          fontFamily: "var(--m3-font, Poppins, sans-serif)",
                          textAlign: "left",
                        }}
                      >
                        {t("Under GHG Protocol Corporate Value Chain (Scope 3) Category 3 guidelines, upstream well-to-tank (WTT) fuel extraction and transmission & distribution (T&D) grid losses are mathematically derived from your facility's Scope 1 natural gas and Scope 2 electricity inventory. Zero vendor invoices are required.")}
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    <div style={{ padding: "14px", borderRadius: "10px", background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff", border: "1px solid var(--m3-outline-variant, #cbd5e1)" }}>
                      <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#4f46e5", fontFamily: "var(--m3-font)" }}>
                        {t("Activity 3A · Upstream WTT")}
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", marginTop: "4px", lineHeight: "1.5" }}>
                        {t("Upstream extraction, refining, and transport of fuels consumed in Scope 1 (~15.5% WTT factor).")}
                      </div>
                    </div>

                    <div style={{ padding: "14px", borderRadius: "10px", background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff", border: "1px solid var(--m3-outline-variant, #cbd5e1)" }}>
                      <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#2563eb", fontFamily: "var(--m3-font)" }}>
                        {t("Activity 3C · Grid T&D Losses")}
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", marginTop: "4px", lineHeight: "1.5" }}>
                        {t("Physical electrical losses occurring during transmission across the regional power grid (5.5%).")}
                      </div>
                    </div>

                    <div style={{ padding: "14px", borderRadius: "10px", background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff", border: "1px solid var(--m3-outline-variant, #cbd5e1)" }}>
                      <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#059669", fontFamily: "var(--m3-font)" }}>
                        {t("Activity 3D · WTT of T&D")}
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", marginTop: "4px", lineHeight: "1.5" }}>
                        {t("Upstream extraction of fuels used in generating electricity lost during transmission (~1.2%).")}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
                    <M3Button
                      variant="filled"
                      size="md"
                      onClick={handleAutoDeriveCategory3}
                      icon={<LuRepeat size={18} />}
                    >
                      {t("Auto-Derive from Scope 1 & 2 Inventory")}
                    </M3Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Human-in-the-Loop Target Row Banner OR Scope & Category Selector */}
                  {selectedRow ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%", textAlign: "left" }}>
                      {/* Target Row Context Card (Quiet M3 surface, clearly indicating the active inventory target) */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "12px",
                          padding: "14px 18px",
                          borderRadius: "var(--m3-radius-md, 12px)",
                          border: "1px solid var(--m3-outline-variant, #cbd5e1)",
                          background: isDark ? "rgba(255, 255, 255, 0.03)" : "var(--m3-surface-container-low, #f8fafc)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flexWrap: "wrap" }}>
                          <div
                            style={{
                              width: "38px",
                              height: "38px",
                              borderRadius: "50%",
                              background: `${activeCategory?.accent || "#659d33"}1a`,
                              color: activeCategory?.accent || "#659d33",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {scopeMeta.icon || <LuFlame size={20} />}
                          </div>

                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <span
                                style={{
                                  fontSize: "14.5px",
                                  fontWeight: 600,
                                  color: "var(--m3-on-surface, #0f172a)",
                                  fontFamily: "var(--m3-font, Poppins, sans-serif)",
                                }}
                              >
                                {selectedRow.description || (typeof selectedRow.category === "object" ? selectedRow.category?.name : selectedRow.category) || t("Inventory Row")}
                              </span>
                              <M3Badge tone="primary" size="sm">
                                {activeScope}
                              </M3Badge>
                              {selectedRow.description && selectedRow.category && (
                                <M3Badge tone="neutral" size="sm">
                                  {typeof selectedRow.category === "object" ? selectedRow.category?.name : selectedRow.category}
                                </M3Badge>
                              )}
                            </div>
                            <span style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                              {t("Linked inventory target for bill and invoice evidence mapping")}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                          {activeSubtype && (
                            <Tooltip title={t("Standard conversion benchmark: {{factor}} {{unit}}", { factor: activeSubtype.factor, unit: activeSubtype.factorUnit })} arrow>
                              <span>
                                <M3Badge tone="neutral" size="md">
                                  {t("Benchmark Factor:")} <strong>{activeSubtype.factor} {activeSubtype.factorUnit}</strong>
                                </M3Badge>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Fuel / Activity Subtype Selection Buttons (Dedicated action section using M3Button) */}
                      {activeCategory?.subtypes?.length > 1 && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            padding: "14px 18px",
                            borderRadius: "var(--m3-radius-md, 12px)",
                            border: "1px solid var(--m3-outline-variant, #cbd5e1)",
                            background: isDark ? "rgba(255, 255, 255, 0.02)" : "var(--m3-surface-container-lowest, #ffffff)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "var(--m3-on-surface, #0f172a)",
                                fontFamily: "var(--m3-font, Poppins, sans-serif)",
                              }}
                            >
                              {t("Select Fuel / Activity Subtype")}
                            </span>
                            <span style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                              {t("Choose the specific fuel or source matching your bills")}
                            </span>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", paddingTop: "2px" }}>
                            {activeCategory.subtypes.map((sub) => {
                              const isSelected = activeSubtype?.name === sub.name;
                              return (
                                <M3Button
                                  key={sub.name}
                                  variant={isSelected ? "filled" : "tonal"}
                                  size="sm"
                                  onClick={() => handleSubtypeSelect(sub)}
                                  icon={isSelected ? <LuCheck size={15} /> : undefined}
                                  tooltip={t("{{desc}} — Factor: {{factor}} {{unit}}", {
                                    desc: sub.desc || sub.name,
                                    factor: sub.factor,
                                    unit: sub.factorUnit,
                                  })}
                                >
                                  {sub.name} ({sub.unit})
                                </M3Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Interactive Scope & Category Selection when no row was selected */
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", textAlign: "left" }}>
                      {/* M3 Segmented Button for Scope Switching */}
                      <SegmentedButton
                        options={Object.keys(SCOPE_DEFINITIONS).map((scKey) => ({
                          value: scKey,
                          label: `${scKey} · ${SCOPE_DEFINITIONS[scKey].label}`,
                        }))}
                        value={activeScope}
                        onChange={(scKey) => {
                          setActiveScope(scKey);
                          const def = SCOPE_DEFINITIONS[scKey];
                          const firstCat = def?.categories?.[0];
                          setActiveCategory(firstCat);
                          const firstSub = firstCat?.subtypes?.[0];
                          setActiveSubtype(firstSub);
                          if (firstSub?.factor != null) setGlobalFactor(firstSub.factor);
                        }}
                        showCheck={true}
                        size="md"
                        block={false}
                        ariaLabel={t("Scope selection tabs")}
                      />

                      {/* Interactive Category Cards using ModeSelector */}
                      <ModeSelector
                        options={(SCOPE_DEFINITIONS[activeScope]?.categories || []).map((cat) => ({
                          value: cat.id,
                          label: cat.label,
                          description: cat.desc,
                          icon: cat.icon,
                          badge: cat.subtypes?.[0]?.unit ? `${cat.subtypes[0].unit}` : undefined,
                        }))}
                        value={activeCategory?.id}
                        onChange={(catId) => {
                          const cat = SCOPE_DEFINITIONS[activeScope]?.categories.find((c) => c.id === catId);
                          setActiveCategory(cat);
                          const firstSub = cat?.subtypes?.[0];
                          setActiveSubtype(firstSub);
                          if (firstSub?.factor != null) setGlobalFactor(firstSub.factor);
                        }}
                        ariaLabel={t("Scope category selector")}
                      />

                      {/* Category Subtypes Selection using M3Button */}
                      {activeCategory?.subtypes?.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            padding: "14px 18px",
                            borderRadius: "var(--m3-radius-md, 12px)",
                            border: "1px solid var(--m3-outline-variant, #cbd5e1)",
                            background: isDark ? "rgba(255, 255, 255, 0.02)" : "var(--m3-surface-container-lowest, #ffffff)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
                            <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--m3-on-surface, #0f172a)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                              {t("Select Fuel / Material Subtype")}
                            </span>
                            {activeSubtype && (
                              <span style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                                {t("Factor:")} <strong>{activeSubtype.factor} {activeSubtype.factorUnit}</strong>
                              </span>
                            )}
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", paddingTop: "2px" }}>
                            {activeCategory.subtypes.map((sub) => {
                              const isSelected = activeSubtype?.name === sub.name;
                              return (
                                <M3Button
                                  key={sub.name}
                                  variant={isSelected ? "filled" : "tonal"}
                                  size="sm"
                                  onClick={() => handleSubtypeSelect(sub)}
                                  icon={isSelected ? <LuCheck size={15} /> : undefined}
                                  tooltip={t("{{desc}} — Factor: {{factor}} {{unit}}", {
                                    desc: sub.desc || sub.name,
                                    factor: sub.factor,
                                    unit: sub.factorUnit,
                                  })}
                                >
                                  {sub.name} ({sub.unit})
                                </M3Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 1A. Available Evidence Bills in Repository Card */}
                  {hasExistingFiles && (
                    <div
                      style={{
                        padding: "16px 20px",
                        borderRadius: "var(--m3-radius-md, 12px)",
                        background: isDark ? "rgba(101, 157, 51, 0.12)" : "rgba(101, 157, 51, 0.08)",
                        border: "1px solid rgba(101, 157, 51, 0.28)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              background: "rgba(101, 157, 51, 0.18)",
                              color: "#4a7c23",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <DescriptionOutlinedIcon style={{ fontSize: "22px" }} />
                          </div>
                          <div style={{ textAlign: "left" }}>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--m3-on-surface, #0f172a)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                              {t("{{count}} Documents Available in {{scope}}", { count: existingFiles.length, scope: activeScope })}
                            </div>
                            <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                              {t("Files already attached in the Evidence Repository. Ready for instant batch extraction.")}
                            </div>
                          </div>
                        </div>

                        <M3Button
                          variant={selectedFiles.length > 0 ? "outlined" : "filled"}
                          size="md"
                          onClick={() => handleStartAnalysis("existing_only")}
                          icon={<AutoAwesomeOutlinedIcon style={{ fontSize: "18px" }} />}
                        >
                          {t("Analyze {{count}} Uploaded {{label}}", { count: existingFiles.length, label: scopeMeta.documentNoun })}
                        </M3Button>
                      </div>

                      {/* Existing Files Chips */}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {existingFiles.map((doc, idx) => {
                          const name = typeof doc === "string" ? doc : doc.name;
                          return (
                            <Chip key={idx} tone="success" size="sm" icon={<LuFileText size={13} />}>
                              {name}
                            </Chip>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 1B. Dropzone Header info */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                    <span style={{ fontSize: "14px", fontWeight: "var(--m3-font-weight-medium, 500)", color: "var(--m3-on-surface, #0f172a)", fontFamily: "var(--m3-font, Poppins, sans-serif)", textAlign: "left" }}>
                      {hasExistingFiles ? t("Or Add Additional Files") : t("Select {{label}}", { label: scopeMeta.documentNoun })}
                    </span>
                    <span style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                      {t("Supported format: PDF")}
                    </span>
                  </div>

                  {/* 1C. Dropzone Container */}
                  <div
                    style={{
                      position: "relative",
                      padding: "44px 20px",
                      borderRadius: "var(--m3-radius-lg, 16px)",
                      border: dragActive
                        ? "2px dashed var(--m3-brand, #659d33)"
                        : "2px dashed var(--m3-outline-variant, #cbd5e1)",
                      background: dragActive
                        ? "var(--m3-surface-container-highest, rgba(101, 157, 51, 0.12))"
                        : isDark ? "rgba(255, 255, 255, 0.02)" : "var(--m3-surface-container-low, #f8fafc)",
                      transition: "all 0.2s cubic-bezier(0.2, 0, 0, 1)",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", pointerEvents: "none" }}>
                      <div
                        style={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "50%",
                          background: "rgba(101, 157, 51, 0.15)",
                          color: "#4a7c23",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DescriptionOutlinedIcon style={{ fontSize: "32px" }} />
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "var(--m3-font-weight-medium, 500)",
                            color: "var(--m3-on-surface, #0f172a)",
                            fontFamily: "var(--m3-font, Poppins, sans-serif)",
                          }}
                        >
                          {t("Drag & drop your {{label}} here, or click to browse", { label: scopeMeta.documentNoun.toLowerCase() })}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "var(--m3-on-surface-variant, #64748b)",
                            fontFamily: "var(--m3-font, Poppins, sans-serif)",
                          }}
                        >
                          {t("Upload vendor bills, invoices, receipts, or waybills (PDF)")}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 1D. Newly Staged Files */}
                  {selectedFiles.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", textAlign: "left" }}>
                      <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                        {t("Newly Added Files ({{count}} files)", { count: selectedFiles.length })}:
                      </span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {selectedFiles.map((file, idx) => (
                          <Chip
                            key={`new-${file.name}-${idx}`}
                            tone="primary"
                            size="sm"
                            icon={<LuFileText size={13} />}
                            onClick={() => handleRemoveFile(idx)}
                            tooltip={t("Click to remove file")}
                          >
                            {file.name} ({(file.size / 1024).toFixed(0)} KB) ✕
                          </Chip>
                        ))}
                      </div>

                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* SECTION 2: Progress State */}
          {isAnalyzing && (
            <div
              style={{
                padding: "28px 24px",
                borderRadius: "var(--m3-radius-lg, 16px)",
                border: "1px solid var(--m3-outline-variant, #cbd5e1)",
                background: isDark ? "rgba(255,255,255,0.02)" : "var(--m3-surface-container-low, #f8fafc)",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  style={{
                    fontFamily: "var(--m3-font, Poppins, sans-serif)",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "var(--m3-on-surface, #0f172a)",
                  }}
                >
                  {progressStatusText}
                </span>
                <span
                  style={{
                    fontFamily: "var(--m3-font, Poppins, sans-serif)",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#659d33",
                  }}
                >
                  {analysisProgress}%
                </span>
              </div>

              <LinearProgress
                variant="determinate"
                value={analysisProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "#659d33",
                    borderRadius: 4,
                  },
                }}
              />
            </div>
          )}

          {/* SECTION 3: Extracted Review Grid */}
          {extractedBills.length > 0 && (
            <>
              {/* Summary KPIs with authentic M3 KpiTile and KpiGrid */}
              <KpiGrid columns={4}>
                <KpiTile
                  label={scopeMeta.categoryType === "fuel_energy" ? t("Derived Items") : t("Items Analyzed")}
                  value={`${liveTotals.validCount} / ${extractedBills.length}`}
                  tone="neutral"
                  icon={<LuFileText size={18} />}
                />
                <KpiTile
                  label={t("Total Activity Quantity")}
                  value={Number(liveTotals.totalUsage).toLocaleString()}
                  unit={liveTotals.unit}
                  tone="scope2"
                  icon={<LuZap size={18} />}
                />
                <KpiTile
                  label={t("Total Spend / Cost")}
                  value={Number(liveTotals.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  unit={liveTotals.currency || ""}
                  tone="cost"
                  icon={<LuGauge size={18} />}
                />
                <KpiTile
                  label={t("Total Carbon Footprint")}
                  value={liveTotals.totalTco2e}
                  unit="tCO₂e"
                  tone="gwp"
                  icon={<LuFlame size={18} />}
                />
              </KpiGrid>

              {/* Interactive Quarterly / Multi-Month Distribution Banner */}
              {hasQuarterlyBills && (
                <Banner tone="info" icon={<LuCalendar size={22} />}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", width: "100%" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "1 1 300px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <strong style={{ fontSize: "14px", color: "var(--m3-on-surface)" }}>
                          {t("Quarterly / Multi-Month Billing Detected")}
                        </strong>
                        <Chip tone="primary" size="sm">
                          {quarterlyMode === "monthly" ? t("Split: 3 Monthly Records") : t("Unified: 1 Record")}
                        </Chip>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant)" }}>
                        {t("One or more bills cover a multi-month period. Choose how you want this recorded in your inventory:")}
                      </div>
                    </div>

                    <SegmentedButton
                      size="sm"
                      showCheck={true}
                      options={[
                        { value: "unified", label: t("Unified (1 Record)") },
                        { value: "monthly", label: t("Split Pro-Rata (3 Monthly)"), icon: <LuSplit size={14} /> },
                      ]}
                      value={quarterlyMode}
                      onChange={handleToggleQuarterlyMode}
                    />
                  </div>
                </Banner>
              )}

              {/* Factor Settings Bar — Clean M3 Elevated Card */}
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "var(--m3-radius-lg, 16px)",
                  border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid var(--m3-outline-variant, #cbd5e1)",
                  background: isDark ? "rgba(255,255,255,0.02)" : "var(--m3-surface-container-low, #f8fafc)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "16px 24px",
                }}
              >
                {scopeMeta.categoryType === "fuel_energy" ? (
                  /* Category 3 Multi-Activity Factor Bar */
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: "1 1 340px", minWidth: 0, textAlign: "left" }}>
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "50%",
                          background: isDark ? "rgba(99, 102, 241, 0.22)" : "rgba(99, 102, 241, 0.12)",
                          color: isDark ? "#a5b4fc" : "#4f46e5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LuRepeat size={22} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--m3-on-surface, #0f172a)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                            {t("GHG Protocol Multi-Activity Derivation")}
                          </span>
                          <Tooltip title={t("Category 3 calculates Activities 3A, 3C, and 3D using DEFRA & Canada NIR standard factors derived from facility Scope 1 and Scope 2 baseline activity.")} arrow>
                            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", color: "var(--m3-on-surface-variant, #64748b)" }}>
                              <LuInfo size={15} />
                            </span>
                          </Tooltip>
                          <Chip tone="primary" size="sm">
                            {t("Scope 1 & 2 Baseline Derived")}
                          </Chip>
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)", lineHeight: 1.45 }}>
                          {t("Mathematically derived from Scope 1 (15.5% WTT) + Scope 2 (5.5% Grid T&D loss + 1.2% WTT of T&D)")}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                      <Chip tone="success" size="md">
                        {t("Multi-Factor Mathematical Model Active")}
                      </Chip>
                    </div>
                  </>
                ) : (
                  /* Standard / Scope 1, 2, 4, 5, 6 Factor Bar */
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: "1 1 340px", minWidth: 0, textAlign: "left" }}>
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "50%",
                          background: isDark ? "rgba(101, 157, 51, 0.22)" : "rgba(101, 157, 51, 0.12)",
                          color: isDark ? "#86efac" : "#4a7c23",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LuGauge size={22} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--m3-on-surface, #0f172a)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                            {t("Emission Factor Benchmark")}
                          </span>
                          <Tooltip title={scopeMeta.factorExplanation || t("Standard conversion factor used to calculate metric tonnes of CO₂ equivalent (tCO₂e) from activity data.")} arrow>
                            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", color: "var(--m3-on-surface-variant, #64748b)" }}>
                              <LuInfo size={15} />
                            </span>
                          </Tooltip>
                          <Chip tone="primary" size="sm">
                            {t("Standard Benchmark")}
                          </Chip>
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)", lineHeight: 1.45 }}>
                          {t("Emissions (tCO₂e) = Activity Quantity × Emission Factor. You can customize the factor on the right.")}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: "14px", flexShrink: 0 }}>
                      {/* Natural Gas unit toggle */}
                      {activeSubtype?.fuelType === "natural_gas" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                            {t("Reporting Unit (1 m³ = 10.55 kWh)")}
                          </span>
                          <SegmentedButton
                            size="sm"
                            options={[
                              { value: "m³", label: "m³ (Volume)" },
                              { value: "kWh", label: "kWh (Energy)" },
                            ]}
                            value={activeSubtype?.unit === "kWh" ? "kWh" : "m³"}
                            onChange={(newU) => handleNaturalGasUnitToggle(newU)}
                          />
                        </div>
                      )}

                      {/* Diesel / IFTA unit toggle */}
                      {activeSubtype?.fuelType === "diesel" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
                            {t("Fuel Unit (1 gal = 3.785 L)")}
                          </span>
                          <SegmentedButton
                            size="sm"
                            options={[
                              { value: "L", label: "Litres (L)" },
                              { value: "gal", label: "Gallons" },
                            ]}
                            value={activeSubtype?.unit === "gal" ? "gal" : "L"}
                            onChange={(newU) => handleDieselUnitToggle(newU)}
                          />
                        </div>
                      )}

                      <div style={{ width: "210px" }}>
                        <M3Field
                          label={t("Factor Value")}
                          type="number"
                          value={globalFactor}
                          onChange={(val) => handleGlobalFactorChange(val)}
                          suffix={scopeMeta.factorUnit}
                          inputProps={{ step: "0.000001", min: "0" }}
                          helper={
                            Number(globalFactor) !== Number(scopeMeta.defaultFactor)
                              ? t("Custom factor applied")
                              : t("Standard benchmark factor")
                          }
                        />
                      </div>
                      {Number(globalFactor) !== Number(scopeMeta.defaultFactor) && (
                        <div style={{ marginBottom: "22px" }}>
                          <M3Button
                            variant="text"
                            size="sm"
                            onClick={() => handleGlobalFactorChange(scopeMeta.defaultFactor)}
                          >
                            {t("Reset")}
                          </M3Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Data Table powered by project AgGrid */}
              <Box
                sx={{
                  borderRadius: "12px",
                  border: `1px solid ${tok.border.table || "var(--m3-outline-variant, #cbd5e1)"}`,
                  overflow: "hidden",
                  background: tok.bg.card || "var(--m3-surface-container-lowest, #ffffff)",
                  height: 380,
                  width: "100%",
                }}
              >
                <AgGrid
                  rowData={extractedBills}
                  columnDefs={columnDefs}
                  getRowId={(params) => `${params.data.file_name}-${params.data.bill_id || params.data.id || Math.random()}`}
                  suppressPagination={true}
                  singleClickEdit={true}
                  onCellValueChanged={handleCellValueChanged}
                  sx={{ height: "100%", width: "100%" }}
                />
              </Box>
            </>
          )}
        </div>
      </div>

      {/* Dialog Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderTop: "1px solid var(--m3-outline-variant, #cbd5e1)",
          background: "var(--m3-surface-container-low, #f8fafc)",
        }}
      >
        <div style={{ textAlign: "left" }}>
          {extractedBills.length > 0 && (
            <span style={{ fontSize: "12px", color: "var(--m3-on-surface-variant, #64748b)", fontFamily: "var(--m3-font, Poppins, sans-serif)" }}>
              {t("Review, verify, and edit values before importing into your emission inventory")}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <M3Button
            variant="outlined"
            size="md"
            onClick={onClose}
            disabled={isSaving || isAnalyzing}
          >
            {extractedBills.length > 0 ? t("Cancel") : t("Close")}
          </M3Button>

          {/* Primary Action Buttons in Modal Footer Before Extraction */}
          {extractedBills.length === 0 && !isAnalyzing && (
            <>
              {selectedFiles.length > 0 && hasExistingFiles && (
                <>
                  <M3Button
                    variant="tonal"
                    size="md"
                    onClick={() => handleStartAnalysis("all")}
                    icon={<AutoAwesomeOutlinedIcon style={{ fontSize: "18px" }} />}
                  >
                    {t("Analyze All ({{count}} Documents)", { count: totalStagedCount })}
                  </M3Button>
                  <M3Button
                    variant="filled"
                    size="md"
                    onClick={() => handleStartAnalysis("new_only")}
                    icon={<AutoAwesomeOutlinedIcon style={{ fontSize: "18px" }} />}
                  >
                    {t("Analyze Newly Added ({{count}})", { count: selectedFiles.length })}
                  </M3Button>
                </>
              )}

              {selectedFiles.length > 0 && !hasExistingFiles && (
                <M3Button
                  variant="filled"
                  size="md"
                  onClick={() => handleStartAnalysis("new_only")}
                  icon={<AutoAwesomeOutlinedIcon style={{ fontSize: "18px" }} />}
                >
                  {t("Analyze {{count}} {{label}}", { count: selectedFiles.length, label: scopeMeta.documentNoun })}
                </M3Button>
              )}

              {selectedFiles.length === 0 && hasExistingFiles && (
                <M3Button
                  variant="filled"
                  size="md"
                  onClick={() => handleStartAnalysis("existing_only")}
                  icon={<AutoAwesomeOutlinedIcon style={{ fontSize: "18px" }} />}
                >
                  {t("Analyze {{count}} Uploaded {{label}}", { count: existingFiles.length, label: scopeMeta.documentNoun })}
                </M3Button>
              )}
            </>
          )}

          {extractedBills.length > 0 && (
            <>
              <M3Button
                variant="tonal"
                size="md"
                onClick={() => {
                  setExtractedBills([]);
                  setSelectedFiles([]);
                }}
                disabled={isSaving || isAnalyzing}
              >
                {t("Upload Different Bills")}
              </M3Button>

              <M3Button
                variant="filled"
                size="md"
                loading={isSaving}
                onClick={handleSaveToInventory}
                icon={<SaveOutlinedIcon style={{ fontSize: "18px" }} />}
              >
                {t("Save Validated Data to Inventory")}
              </M3Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
